import React, { FC } from 'react';
import { useSite } from '@v2/shared/lib/site';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import Container from '@v2/shared/components/Container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import FooterLink from './FooterLink';
import FooterIconLink from './FooterIconLink';
import BottomBanner from './BottomBanner';
import NewsletterSignup from './NewsletterSignup';
import LinkGroup from './LinkGroup';
import LinkGroupHeader from './LinkGroupHeader';
import PaymentMethodIcon from './PaymentMethodIcon';
import TrustSeals from './TrustSeals';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import {
  faCcApplePay,
  faCcAmex,
  faCcVisa,
  faCcMastercard,
  faCcPaypal,
  faCcDiscover,
  faSquareXTwitter,
  faPinterestSquare,
  faInstagramSquare,
} from '@fortawesome/free-brands-svg-icons';

/** The Gifts & More landing page. @see GiftsPagePropsBuilder */
const GIFTS_AND_MORE = '/categories/gifts';

type Props = {
  /**
   * Whether to show the Klaviyo newsletter embed above the footer.
   *
   * Defaults to on, because nearly every page wants it. blackfriday and
   * packdeals_categories do not, and say so with
   * `{#set:settings.hideNewsletterOnFooter#}Y{#/set#}` in their settings block — the
   * same mechanism they already use to ask for the legacy navbar. The legacy footer
   * template reads that setting and passes it here.
   */
  showNewsletter?: boolean;
  /**
   * The year for the copyright line, from the layout's props. Absent on the legacy
   * mount, which renders in the browser — see BottomBanner.
   */
  copyrightYear?: number;
};

/**
 * Four columns: where to shop, where to get help, who we are, and why to believe us.
 *
 * The legal links are not among them any more — they are in the bottom bar with the
 * copyright, which is where a visitor looks for them. See BottomBanner, which owns the
 * Do Not Sell link and its consent gate along with them.
 *
 * One column on a phone rather than the two it used to be, because the widest label here
 * ("Do Not Sell…" aside, "Bulk & Corporate Orders") does not fit a half-width column on a
 * 375px screen without wrapping every other row, and a column of wrapped two-line rows is
 * not a list a thumb can use. Two columns return at md, four at lg.
 */
const Footer: FC<Props> = ({ showNewsletter = true, copyrightYear }) => {
  // Books, Gifts & More and the bulk order page only exist on the US site —
  // /categories/books, /categories/gifts and /bulk-order all 404 on discountmags.ca and
  // on /au/ — so none of those links is drawn elsewhere. Everything else in these
  // columns is served on all four storefronts.
  const { country } = useSite();

  // Every internal link goes through buildUrl. The paths below are written from the site
  // root, and AU serves from https://www.discountmags.com/au/ — rendered bare, each one
  // would walk an AU visitor out of /au/ and onto the US site. The help centre and social
  // links are absolute and stay as they are.
  const { buildUrl } = useUrlGenerator();

  return (
    <>
      {showNewsletter && <NewsletterSignup />}
      <footer className="bg-slate-950 py-10 md:pb-14 md:pt-12 lg:pt-14 lg:pb-8 flex items-start justify-center w-full">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 w-full">
            <LinkGroup>
              <LinkGroupHeader>Shop</LinkGroupHeader>
              <FooterLink href={buildUrl('/bestsellers')} ariaLabel="Shop Best Sellers">
                Best Sellers
              </FooterLink>
              <FooterLink href={buildUrl('/categories/digital/deals')}>Digital Deals</FooterLink>
              <FooterLink href={buildUrl('/categories/digital')}>Digital Magazines</FooterLink>
              {country === 'US' && <FooterLink href={buildUrl('/categories/books')}>Books</FooterLink>}
              {country === 'US' && <FooterLink href={buildUrl(GIFTS_AND_MORE)}>Gifts &amp; More</FooterLink>}
              <FooterLink href={buildUrl('/categories/food-and-beverage')}>Food &amp; Beverage</FooterLink>
              <FooterLink href={buildUrl('/categories/home-and-garden')}>Home &amp; Garden</FooterLink>
              <FooterLink href={buildUrl('/categories/sports-and-recreation')}>Sports &amp; Recreation</FooterLink>
            </LinkGroup>
            <LinkGroup>
              <LinkGroupHeader>Help</LinkGroupHeader>
              <FooterLink href={buildUrl('/help')} emphasised>
                Help Center
              </FooterLink>
              {/* The three questions support is asked most, answered outright rather than
                  by dropping the visitor on the help centre's front page. */}
              <FooterLink href={buildUrl('/help/missed-issue')}>
                Where{`'`}s My Issue?
              </FooterLink>
              <FooterLink href={buildUrl('/help/change-address')}>
                Change My Address
              </FooterLink>
              <FooterLink href={buildUrl('/help/cancel-vs-turn-off-renewal')}>
                Renew or Cancel
              </FooterLink>
              <FooterLink href={buildUrl('/shipping')}>Delivery Times</FooterLink>
              <FooterLink href={buildUrl('/refund-policy')}>Refund Policy</FooterLink>
              <FooterLink href={buildUrl('/customer?xCmd=account')}>My Account</FooterLink>
              {/* No display class of its own: the icon is an inline svg, so it sits
                  beside the label in the row's flex layout on a phone and in normal
                  inline flow above lg. Adding `lg:flex` here would collide with
                  FooterLink's own `lg:block` — same property, same variant, and `cn`
                  settles nothing. */}
              <FooterLink href={buildUrl('/contact')} emphasised>
                {/* The pro packages ship their own copy of fontawesome-common-types, so
                    the IconDefinition imported here is a different type from the one
                    FontAwesomeIcon's signature names. Cast at the call site, as
                    Pages/AboutUs does. */}
                <FontAwesomeIcon icon={faEnvelope as IconProp} aria-hidden className="mr-2" />
                <span>Contact Us</span>
              </FooterLink>
            </LinkGroup>
            <LinkGroup>
              <LinkGroupHeader>Company</LinkGroupHeader>
              <FooterLink href={buildUrl('/about-us')}>About DiscountMags</FooterLink>
              <FooterLink href={buildUrl('/guarantee')}>Our Guarantee</FooterLink>
              {/* Next to the guarantee on purpose: the two pages answer the same question,
                  which is what a shopper is promised before they buy. */}
              <FooterLink href={buildUrl('/discountlock')}>DiscountLock</FooterLink>
              <FooterLink href={buildUrl('/reviews')}>Customer Reviews</FooterLink>
              <FooterLink href={buildUrl('/affiliate-program')}>Affiliate Program</FooterLink>
              {country === 'US' && (
                <FooterLink href={buildUrl('/bulk-order')}>Bulk &amp; Corporate Orders</FooterLink>
              )}
            </LinkGroup>
            <div>
              <LinkGroup>
                <LinkGroupHeader>Rated Excellent</LinkGroupHeader>
                <TrustSeals />
              </LinkGroup>
              <LinkGroup>
                <LinkGroupHeader>Secure Payment</LinkGroupHeader>
                <PaymentMethodIcon icon={faCcVisa} />
                <PaymentMethodIcon icon={faCcMastercard} />
                <PaymentMethodIcon icon={faCcAmex} />
                <PaymentMethodIcon icon={faCcDiscover} />
                <PaymentMethodIcon icon={faCcPaypal} />
                <PaymentMethodIcon icon={faCcApplePay} />
              </LinkGroup>
              <LinkGroup>
                <LinkGroupHeader>Follow Us</LinkGroupHeader>
                <div className="flex gap-2">
                  <FooterIconLink
                    href="https://instagram.com/discountmagsdotcom"
                    target="_blank"
                    referrerPolicy="no-referrer"
                    ariaLabel="Follow DiscountMags on Instagram"
                    icon={faInstagramSquare}
                  />
                  {/* Twitter is X. faSquareXTwitter rather than the bare mark so it sits
                      in the same tile shape as the two beside it. */}
                  <FooterIconLink
                    href="https://twitter.com/discountmags"
                    target="_blank"
                    referrerPolicy="no-referrer"
                    ariaLabel="Follow DiscountMags on X"
                    icon={faSquareXTwitter}
                  />
                  <FooterIconLink
                    href="https://pinterest.com/discountmags/"
                    target="_blank"
                    referrerPolicy="no-referrer"
                    ariaLabel="Follow DiscountMags on Pinterest"
                    icon={faPinterestSquare}
                  />
                </div>
              </LinkGroup>
            </div>
          </div>
        </Container>
      </footer>
      <BottomBanner year={copyrightYear} />
    </>
  );
};

export default Footer;
